import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const chapters = [
  {
    num: "01",
    title: "Built by title people.",
    copy: "Northwest Tech Alliance wasn't founded in a lab. It was built by title professionals who cleared files, cured defects, and lived the work — then decided the industry deserved software that thinks the way they do.",
  },
  {
    num: "02",
    title: "Rooted in the Northwest.",
    copy: "Evergreens, granite, and a bias for doing things right the first time. The Pacific Northwest shaped how we build: quiet, rigorous, and engineered to hold up under weather.",
  },
  {
    num: "03",
    title: "Deployed for the nation.",
    copy: "Under Northwest Tech Alliance, the same products that serve Northwest Title Alliance in the Pacific Northwest now reach title agents, lenders, attorneys, and agencies in all fifty states.",
  },
];

export const About = () => (
  <section id="about" className="relative bg-fog text-navy py-24 md:py-36 overflow-hidden" data-testid="about-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24"
      >
        <div className="md:col-span-3">
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech">02 / The Alliance</span>
        </div>
        <div className="md:col-span-9">
          <h2 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
            A manifesto in
            <br />
            three chapters<span className="text-tech">.</span>
          </h2>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE }}
          className="md:col-span-5 relative"
        >
          <div className="border border-navy/15 px-8 py-7 mb-8 flex items-center justify-center" data-testid="about-brand-plate">
            <img
              src="/assets/nwta-logo-full.png"
              alt="Northwest Tech Alliance — AI · Software · Innovation"
              className="w-full max-w-[320px]"
            />
          </div>
          <div className="relative overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 0 96%)" }}>
            <img
              src="/assets/about-aerial.jpg"
              alt="Aerial view of a Pacific Northwest city and river"
              className="w-full h-[420px] md:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-navy/20" />
          </div>
          <div className="mt-6 flex items-center gap-4">
            <span className="h-px w-10 bg-tech" />
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-navy/50">
              Northwest Title Alliance — DBA Northwest Tech Alliance
            </p>
          </div>
        </motion.div>

        <div className="md:col-span-6 md:col-start-7 flex flex-col justify-center">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
              className="group border-t border-navy/15 py-8 md:py-10 first:border-t-0 first:pt-0"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <div className="flex items-baseline gap-6">
                <span className="font-mono-accent text-sm text-tech group-hover:text-evergreen-bright transition-colors duration-300">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-display font-semibold tracking-tight text-2xl md:text-3xl mb-3 group-hover:translate-x-1 transition-transform duration-300">
                    {c.title}
                  </h3>
                  <p className="text-sm md:text-base text-navy/60 leading-relaxed max-w-lg">{c.copy}</p>
                </div>
              </div>
            </motion.div>
          ))}

          <motion.a
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            href="https://northwesttitlealliance.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-title-site-link"
            className="mt-6 inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.22em] uppercase text-tech hover:text-navy transition-colors duration-300"
          >
            Visit Northwest Title Alliance <ArrowUpRight className="h-4 w-4" />
          </motion.a>
        </div>
      </div>
    </div>
  </section>
);
